package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.ChecklistAlojamentoCatalog
import br.com.htms.inspeccaocampo.data.FormChecklistAlojamento
import br.com.htms.inspeccaocampo.data.FormComunicacaoOcorrencias
import br.com.htms.inspeccaocampo.data.FormRelatorioFoto
import br.com.htms.inspeccaocampo.data.InspecaoCompleta
import br.com.htms.inspeccaocampo.data.ItemConformidade
import br.com.htms.inspeccaocampo.data.RelatorioFotoCatalog
import br.com.htms.inspeccaocampo.data.RespostaConformidade
import br.com.htms.inspeccaocampo.data.RespostaTernaria
import br.com.htms.inspeccaocampo.platform.PlatformImage
import br.com.htms.inspeccaocampo.platform.PlatformServices
import br.com.htms.inspeccaocampo.platform.formatDateTimeBr
import br.com.htms.inspeccaocampo.util.ImageHeaders
import br.com.htms.inspeccaocampo.platform.ImageSize
import kotlin.math.max
import kotlin.math.min

/**
 * PDF no mesmo padrão visual do APK ([PdfExporter] + [PdfLayout]):
 * cabeçalho logo+banner, tabelas com borda, assinaturas em caixa, 1 página por foto.
 */
object JpegPdfBuilder {
    private const val PAGE_W = 595.0
    private const val PAGE_H = 842.0
    private const val MARGIN = 40.0
    private val CONTENT_W get() = PAGE_W - MARGIN * 2
    private const val COL1_RATIO = 0.36

    /** Gravado no /Info do PDF — fingerprint forense de qual build gerou o arquivo. */
    const val PDF_BUILD_ID = "20260728aloj1"

    private data class PageDesc(
        val contentStream: String,
        val imageIndices: List<Int> = emptyList()
    )

    /** Índices 0-based referenciados por `/ImN Do` no content stream. */
    internal fun imageIndicesFromContent(contentStream: String): List<Int> =
        Regex("""/Im(\d+)\s+Do""")
            .findAll(contentStream)
            .map { it.groupValues[1].toInt() - 1 }
            .filter { it >= 0 }
            .distinct()
            .toList()

    suspend fun build(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.form
        val layout = LayoutEngine()

        // Cabeçalho (logo + banner), como no APK
        val logoJpeg = loadHeaderJpeg("logo_htms.png")
        val bannerJpeg = loadHeaderJpeg("topo_banner.jpg")
        layout.drawHeader(logoJpeg, bannerJpeg)

        layout.drawSectionTitle("Dados gerais")
        layout.drawTable(
            buildList {
                add("TST Responsável" to f.tstResponsavel)
                add("Data e Horário" to f.dataHorarioFormatado())
                add("Obra" to f.obra.ifBlank { "-" })
                add("Efetivo" to f.efetivo)
                add("Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' '))
                f.linkGoogleMaps()?.let { add("Google Maps" to it) }
                add("Responsável Frente" to f.responsavelFrente)
                add("Descrição da Atividade" to f.descricaoAtividade)
            }
        )

        layout.drawSectionTitle("Verificações de campo")
        layout.drawTable(
            f.itens.map { item ->
                val resp = item.resposta?.rotulo ?: "-"
                val valor = buildString {
                    append("Resposta: $resp")
                    if (item.resposta == RespostaTernaria.NAO || item.resposta == RespostaTernaria.NA) {
                        if (item.justificativa.isNotBlank()) {
                            append("\nJustificativa: ${item.justificativa}")
                        }
                    }
                }
                item.titulo to valor
            }
        )

        layout.drawSectionTitle("Desvios, ações e conclusão")
        layout.drawTable(
            listOf(
                "Desvios Encontrados" to f.desviosEncontrados.textoExportacao(),
                "Ações Corretivas" to f.acoesCorretivas.textoExportacao(),
                "Informações adicionais" to f.informacoesAdicionais.ifBlank { "—" },
                "Conclusão" to f.conclusao
            )
        )

        layout.drawSectionTitle("Assinaturas")
        layout.drawSignature("SUPERVISOR", f.assinaturaSupervisor)
        layout.drawSignature("SESMT", f.assinaturaSesmt)
        layout.drawFooter()

        f.fotosParaExportacao().forEachIndexed { index, (secao, foto) ->
            val resolved = resolveFotoJpeg(foto)
            layout.newPage()
            layout.drawSectionTitle("Foto ${index + 1}")
            layout.drawTable(
                listOf(
                    "Seção" to secao,
                    "Número" to (index + 1).toString(),
                    "Data/Hora" to formatDateTimeBr(foto.capturadoEm),
                    "GPS da foto" to when {
                        foto.latitude != null && foto.longitude != null ->
                            "${foto.latitude}, ${foto.longitude}"
                        f.latitude != null && f.longitude != null ->
                            "${f.latitude}, ${f.longitude}"
                        else -> "GPS não capturado"
                    }
                )
            )
            if (resolved != null) {
                layout.drawPhoto(resolved.first, resolved.second.width, resolved.second.height)
            } else {
                // Evita “sumir” a foto no PDF sem feedback (falha comum no mobile web).
                layout.drawTable(
                    listOf(
                        "Status" to
                            "Foto anexada no formulário, mas os bytes não puderam ser " +
                            "carregados neste dispositivo. Tire a foto novamente ou " +
                            "exporte com a mesma sessão do navegador (sem limpar dados).",
                        "Path" to foto.path.take(120)
                    )
                )
            }
            layout.drawFooter()
        }

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    suspend fun buildRelatorio(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.relatorioFoto ?: FormRelatorioFoto()
        val layout = LayoutEngine()

        val logoJpeg = loadHeaderJpeg("logo_htms.png")
        val bannerJpeg = loadHeaderJpeg("topo_banner.jpg")
        layout.drawHeader(logoJpeg, bannerJpeg)

        layout.drawSectionTitle("Legenda")
        layout.drawTable(listOf("Opções" to RespostaConformidade.LEGENDA_SIM_NAO))

        layout.drawSectionTitle("Dados gerais")
        layout.drawTable(
            buildList {
                add("Responsável pelo Alojamento" to f.responsavelAlojamento)
                add("Endereço" to f.endereco)
                add("Data início contrato" to f.dataInicioContrato)
                add("Obra" to f.obra.ifBlank { "-" })
                add("Data e Horário" to f.dataHorarioFormatado())
                add("Capacidade total de pessoas" to f.capacidadeTotalPessoas)
                add("Quantidade de funcionários" to f.quantidadeFuncionarios)
                add("Quantidade de quartos" to f.quantidadeQuartos)
                add("Quantidade de camas" to f.quantidadeCamas)
                add("Quantidade de armários" to f.quantidadeArmarios)
                add("Quantidade de banheiros" to f.quantidadeBanheiros)
                add("Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' '))
                if (f.latitude != null && f.longitude != null) {
                    add("Google Maps" to "https://maps.google.com/?q=${f.latitude},${f.longitude}")
                }
            }
        )

        RelatorioFotoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            layout.drawSectionTitle(secao.titulo)
            layout.drawTable(
                itensSecao.map { item ->
                    item.tituloNumerado to textoItemConformidade(item, usarSimNao = true)
                }
            )
        }

        layout.drawSectionTitle(RelatorioFotoCatalog.SECAO_OBSERVACOES)
        layout.drawTable(
            listOf("Observações" to f.observacoesGerais.ifBlank { "—" })
        )

        layout.drawFooter()

        f.fotosParaExportacao().forEachIndexed { index, (secao, foto) ->
            val resolved = resolveFotoJpeg(foto)
            layout.newPage()
            layout.drawSectionTitle("Foto ${index + 1}")
            layout.drawTable(
                listOf(
                    "Seção" to secao,
                    "Número" to (index + 1).toString(),
                    "Data/Hora" to formatDateTimeBr(foto.capturadoEm),
                    "GPS da foto" to when {
                        foto.latitude != null && foto.longitude != null ->
                            "${foto.latitude}, ${foto.longitude}"
                        f.latitude != null && f.longitude != null ->
                            "${f.latitude}, ${f.longitude}"
                        else -> "GPS não capturado"
                    }
                )
            )
            if (resolved != null) {
                layout.drawPhoto(resolved.first, resolved.second.width, resolved.second.height)
            } else {
                layout.drawTable(
                    listOf(
                        "Status" to
                            "Foto anexada no formulário, mas os bytes não puderam ser " +
                            "carregados neste dispositivo.",
                        "Path" to foto.path.take(120)
                    )
                )
            }
            layout.drawFooter()
        }

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    suspend fun buildChecklist(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.checklistAlojamento ?: FormChecklistAlojamento()
        val layout = LayoutEngine()

        val logoJpeg = loadHeaderJpeg("logo_htms.png")
        val bannerJpeg = loadHeaderJpeg("topo_banner.jpg")
        layout.drawHeader(logoJpeg, bannerJpeg)

        layout.drawSectionTitle("Legenda")
        layout.drawTable(listOf("Conformidade" to RespostaConformidade.LEGENDA))

        layout.drawSectionTitle("Dados gerais")
        layout.drawTable(
            listOf(
                "Endereço" to f.endereco,
                "Obra" to f.obra.ifBlank { "-" },
                "Data e Horário" to f.dataHorarioFormatado(),
                "Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' ')
            )
        )

        ChecklistAlojamentoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            layout.drawSectionTitle(secao.titulo)
            layout.drawTable(
                itensSecao.map { item ->
                    item.tituloNumerado to textoItemConformidade(item)
                }
            )
        }

        layout.drawSectionTitle("Liberado para uso")
        layout.drawTable(
            listOf("Resposta" to (f.liberadoParaUso?.rotulo ?: "-"))
        )

        if (f.acoesCorrecao.isNotEmpty()) {
            layout.drawSectionTitle("Ações de correção")
            layout.drawTable(
                f.acoesCorrecao.mapIndexed { index, acao ->
                    "Ação ${index + 1}" to buildString {
                        if (acao.item.isNotBlank()) append("Item: ${acao.item}\n")
                        if (acao.acao.isNotBlank()) append("Ação: ${acao.acao}\n")
                        if (acao.responsavel.isNotBlank()) append("Responsável: ${acao.responsavel}\n")
                        if (acao.prazo.isNotBlank()) append("Prazo: ${acao.prazo}")
                    }.trim()
                }
            )
        }

        layout.drawSectionTitle("Assinaturas")
        layout.drawTable(
            listOf(
                "Inspecionado por" to assinaturaTexto(f.inspecionadoPor.nome, f.inspecionadoPor.data),
                "Acompanhado por" to assinaturaTexto(f.acompanhadoPor.nome, f.acompanhadoPor.data),
                "Engenheiro residente" to assinaturaTexto(f.engenheiroResidente.nome, f.engenheiroResidente.data)
            )
        )
        layout.drawSignature("Inspecionado por", f.inspecionadoPor.assinatura)
        layout.drawSignature("Acompanhado por", f.acompanhadoPor.assinatura)
        layout.drawSignature("Engenheiro residente", f.engenheiroResidente.assinatura)
        layout.drawFooter()

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    /** PDF único: Parte 1 (fotográfico) + Parte 2 (checklist), mesmo mold HTMS. */
    suspend fun buildRelatorioAlojamento(inspecao: InspecaoCompleta): ByteArray {
        val f1 = inspecao.relatorioFoto ?: FormRelatorioFoto()
        val f2 = inspecao.checklistAlojamento ?: FormChecklistAlojamento()
        val layout = LayoutEngine()

        val logoJpeg = loadHeaderJpeg("logo_htms.png")
        val bannerJpeg = loadHeaderJpeg("topo_banner.jpg")
        layout.drawHeader(logoJpeg, bannerJpeg)

        layout.drawSectionTitle("Relatório Alojamento — Parte 1 (Fotográfico)")
        layout.drawTable(listOf("Opções" to RespostaConformidade.LEGENDA_SIM_NAO))

        layout.drawSectionTitle("Dados gerais")
        layout.drawTable(
            buildList {
                add("Responsável pelo Alojamento" to f1.responsavelAlojamento)
                add("Endereço" to f1.endereco)
                add("Data início contrato" to f1.dataInicioContrato)
                add("Obra" to f1.obra.ifBlank { "-" })
                add("Data e Horário" to f1.dataHorarioFormatado())
                add("Capacidade total de pessoas" to f1.capacidadeTotalPessoas)
                add("Quantidade de funcionários" to f1.quantidadeFuncionarios)
                add("Quantidade de quartos" to f1.quantidadeQuartos)
                add("Quantidade de camas" to f1.quantidadeCamas)
                add("Quantidade de armários" to f1.quantidadeArmarios)
                add("Quantidade de banheiros" to f1.quantidadeBanheiros)
                add("Local (GPS)" to f1.localTexto.replace('\n', ' ').replace('\r', ' '))
                if (f1.latitude != null && f1.longitude != null) {
                    add("Google Maps" to "https://maps.google.com/?q=${f1.latitude},${f1.longitude}")
                }
            }
        )

        RelatorioFotoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f1.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            layout.drawSectionTitle(secao.titulo)
            layout.drawTable(
                itensSecao.map { item ->
                    item.tituloNumerado to textoItemConformidade(item, usarSimNao = true)
                }
            )
        }

        layout.drawSectionTitle(RelatorioFotoCatalog.SECAO_OBSERVACOES)
        layout.drawTable(
            listOf("Observações" to f1.observacoesGerais.ifBlank { "—" })
        )
        layout.drawFooter()

        f1.fotosParaExportacao().forEachIndexed { index, (secao, foto) ->
            val resolved = resolveFotoJpeg(foto)
            layout.newPage()
            layout.drawSectionTitle("Parte 1 — Foto ${index + 1}")
            layout.drawTable(
                listOf(
                    "Seção" to secao,
                    "Número" to (index + 1).toString(),
                    "Data/Hora" to formatDateTimeBr(foto.capturadoEm),
                    "GPS da foto" to when {
                        foto.latitude != null && foto.longitude != null ->
                            "${foto.latitude}, ${foto.longitude}"
                        f1.latitude != null && f1.longitude != null ->
                            "${f1.latitude}, ${f1.longitude}"
                        else -> "GPS não capturado"
                    }
                )
            )
            if (resolved != null) {
                layout.drawPhoto(resolved.first, resolved.second.width, resolved.second.height)
            } else {
                layout.drawTable(
                    listOf(
                        "Status" to
                            "Foto anexada no formulário, mas os bytes não puderam ser " +
                            "carregados neste dispositivo.",
                        "Path" to foto.path.take(120)
                    )
                )
            }
            layout.drawFooter()
        }

        layout.newPage()
        layout.drawHeader(logoJpeg, bannerJpeg)
        layout.drawSectionTitle("Relatório Alojamento — Parte 2 (Checklist)")
        layout.drawTable(listOf("Conformidade" to RespostaConformidade.LEGENDA))

        layout.drawSectionTitle("Dados gerais")
        layout.drawTable(
            listOf(
                "Endereço" to f2.endereco,
                "Obra" to f2.obra.ifBlank { "-" },
                "Data e Horário" to f2.dataHorarioFormatado(),
                "Local (GPS)" to f2.localTexto.replace('\n', ' ').replace('\r', ' ')
            )
        )

        ChecklistAlojamentoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f2.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            layout.drawSectionTitle(secao.titulo)
            layout.drawTable(
                itensSecao.map { item ->
                    item.tituloNumerado to textoItemConformidade(item)
                }
            )
        }

        layout.drawSectionTitle("Liberado para uso")
        layout.drawTable(
            listOf("Resposta" to (f2.liberadoParaUso?.rotulo ?: "-"))
        )

        if (f2.acoesCorrecao.isNotEmpty()) {
            layout.drawSectionTitle("Ações de correção")
            layout.drawTable(
                f2.acoesCorrecao.mapIndexed { index, acao ->
                    "Ação ${index + 1}" to buildString {
                        if (acao.item.isNotBlank()) append("Item: ${acao.item}\n")
                        if (acao.acao.isNotBlank()) append("Ação: ${acao.acao}\n")
                        if (acao.responsavel.isNotBlank()) append("Responsável: ${acao.responsavel}\n")
                        if (acao.prazo.isNotBlank()) append("Prazo: ${acao.prazo}")
                    }.trim()
                }
            )
        }

        layout.drawSectionTitle("Assinaturas")
        layout.drawTable(
            listOf(
                "Inspecionado por" to assinaturaTexto(f2.inspecionadoPor.nome, f2.inspecionadoPor.data),
                "Acompanhado por" to assinaturaTexto(f2.acompanhadoPor.nome, f2.acompanhadoPor.data),
                "Engenheiro residente" to assinaturaTexto(f2.engenheiroResidente.nome, f2.engenheiroResidente.data)
            )
        )
        layout.drawSignature("Inspecionado por", f2.inspecionadoPor.assinatura)
        layout.drawSignature("Acompanhado por", f2.acompanhadoPor.assinatura)
        layout.drawSignature("Engenheiro residente", f2.engenheiroResidente.assinatura)
        layout.drawFooter()

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    suspend fun buildComunicacao(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.comunicacaoOcorrencias ?: FormComunicacaoOcorrencias()
        val layout = LayoutEngine()

        val logoJpeg = loadHeaderJpeg("logo_htms.png")
        val bannerJpeg = loadHeaderJpeg("topo_banner.jpg")
        layout.drawHeader(logoJpeg, bannerJpeg)

        layout.drawSectionTitle("Comunicação de ocorrências")
        layout.drawTable(
            listOf(
                "Descrição" to "Acidente, incidentes e quase acidentes"
            )
        )

        layout.drawSectionTitle("Dados da obra")
        layout.drawTable(
            buildList {
                add("Cliente" to f.cliente)
                add("Nome da obra" to f.obra.ifBlank { "-" })
                add("Data/hora registro" to f.dataHorarioFormatado())
                add("Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' '))
                f.linkGoogleMaps()?.let { add("Google Maps" to it) }
            }
        )

        layout.drawSectionTitle("Caracterização")
        layout.drawTable(
            listOf(
                "Caracterização" to (f.caracterizacao?.rotulo ?: "-"),
                "Tipo" to (f.tipo?.rotulo ?: "-")
            )
        )

        layout.drawSectionTitle("Identificação do evento")
        layout.drawTable(
            listOf(
                "Data e hora do ocorrido" to f.dataHoraOcorrido,
                "Local exato" to f.localExato,
                "Quem está reportando" to f.quemReportando,
                "Tipo de evento" to (f.tipoEvento?.rotulo ?: "-")
            )
        )

        layout.drawSectionTitle("Dados do Envolvido")
        layout.drawTable(
            listOf(
                "Nome" to f.envolvidoNome,
                "Função" to f.envolvidoFuncao
            )
        )

        layout.drawSectionTitle("Responsável pela atividade")
        layout.drawTable(
            listOf(
                "Responsável pela atividade" to f.responsavelAtividade,
                "Cargo" to f.responsavelCargo,
                "Estava no local" to (f.responsavelEstavaNoLocal?.rotulo ?: "-")
            )
        )

        layout.drawSectionTitle("Dados da Ocorrência")
        layout.drawTable(
            listOf(
                "Data do acidente" to f.dataAcidente,
                "Hora do acidente" to f.horaAcidente,
                "Horas trabalhadas" to f.horasTrabalhadas,
                "Local do acidente" to f.localAcidente,
                "Parte do corpo lesionada" to f.parteCorpoLesionada,
                "Agente causador da lesão" to f.agenteCausador,
                "Natureza da lesão" to f.naturezaLesao
            )
        )

        layout.drawSectionTitle("Ações tomadas de imediato")
        layout.drawTable(
            listOf("Ações" to f.acoesTomadasImediato.ifBlank { "—" })
        )

        layout.drawSectionTitle("Descrição do Evento")
        layout.drawTable(
            listOf("Descrição" to f.descricaoEvento.ifBlank { "—" })
        )
        layout.drawFooter()

        f.fotosParaExportacao().forEachIndexed { index, (titulo, foto) ->
            val resolved = resolveFotoJpeg(foto)
            layout.newPage()
            layout.drawSectionTitle("Foto ${index + 1}")
            layout.drawTable(
                listOf(
                    "Seção" to titulo,
                    "Número" to (index + 1).toString(),
                    "Data/Hora" to formatDateTimeBr(foto.capturadoEm),
                    "GPS da foto" to when {
                        foto.latitude != null && foto.longitude != null ->
                            "${foto.latitude}, ${foto.longitude}"
                        f.latitude != null && f.longitude != null ->
                            "${f.latitude}, ${f.longitude}"
                        else -> "GPS não capturado"
                    }
                )
            )
            if (resolved != null) {
                layout.drawPhoto(resolved.first, resolved.second.width, resolved.second.height)
            } else {
                layout.drawTable(
                    listOf(
                        "Status" to
                            "Foto anexada no formulário, mas os bytes não puderam ser " +
                            "carregados neste dispositivo.",
                        "Path" to foto.path.take(120)
                    )
                )
            }
            layout.drawFooter()
        }

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    private fun textoItemConformidade(
        item: ItemConformidade,
        usarSimNao: Boolean = false
    ): String = buildString {
        val respostaTxt = when {
            item.resposta == null -> "-"
            usarSimNao -> item.resposta.rotuloSimNao
            else -> item.resposta.codigo
        }
        append(respostaTxt)
        if (item.valorExtra.isNotBlank()) {
            val label = item.labelExtra.ifBlank { "Valor" }
            append("\n$label: ${item.valorExtra}")
        }
        if (item.resposta == RespostaConformidade.NC || item.resposta == RespostaConformidade.NA) {
            if (item.justificativa.isNotBlank()) {
                append("\nJustificativa: ${item.justificativa}")
            }
        }
    }

    private fun assinaturaTexto(nome: String, data: String): String = buildString {
        if (nome.isNotBlank()) append(nome)
        if (data.isNotBlank()) {
            if (isNotEmpty()) append(" — ")
            append(data)
        }
        if (isEmpty()) append("—")
    }

    /**
     * 1) Base64 embutido no form (Web)
     * 2) loadImageBytes (memória/IDB/R2)
     * 3) toJpegBytes com fallback para JPEG cru se reencode falhar
     */
    suspend fun resolveFotoJpeg(
        foto: br.com.htms.inspeccaocampo.data.FotoAnexo
    ): Pair<ByteArray, ImageSize>? {
        val raw = foto.conteudoBase64
            ?.takeIf { it.isNotBlank() }
            ?.let { PlatformServices.decodeBase64(it) }
            ?.takeIf { it.isNotEmpty() }
            ?: PlatformServices.loadImageBytes(foto.path)
            ?: return null
        val jpeg = PlatformImage.toJpegBytes(raw)?.takeIf { isJpeg(it) }
            ?: raw.takeIf { isJpeg(it) }
            ?: return null
        // Dimensões: SOF → imageSize → fallback (melhor embutir distorcido que placeholder).
        val size = ImageHeaders.size(jpeg)
            ?: PlatformImage.imageSize(jpeg)
            ?: ImageSize(1024, 768)
        return jpeg to size
    }

    private suspend fun loadHeaderJpeg(assetName: String): Pair<ByteArray, Pair<Int, Int>>? {
        val raw = PlatformServices.loadBundledAsset(assetName) ?: return null
        val jpeg = PlatformImage.toJpegBytes(raw)?.takeIf { isJpeg(it) } ?: return null
        // Mesmo fallback de resolveFotoJpeg — não descartar logo/banner se SOF falhar.
        val size = ImageHeaders.size(jpeg)
            ?: PlatformImage.imageSize(jpeg)
            ?: return null
        return jpeg to (size.width to size.height)
    }

    private fun isJpeg(bytes: ByteArray): Boolean =
        bytes.size > 2 && bytes[0] == 0xFF.toByte() && bytes[1] == 0xD8.toByte()

    private class LayoutEngine {
        val jpegs = mutableListOf<ByteArray>()
        private val pages = mutableListOf<PageDesc>()
        private val ops = StringBuilder()
        private val pageImageIdx = mutableListOf<Int>()
        private var y = PAGE_H - MARGIN

        fun finishPages(): List<PageDesc> {
            flushPage()
            return pages.ifEmpty { listOf(PageDesc("")) }
        }

        fun newPage() {
            flushPage()
            y = PAGE_H - MARGIN
        }

        private fun flushPage() {
            if (ops.isEmpty() && pageImageIdx.isEmpty()) return
            pages += PageDesc(ops.toString(), pageImageIdx.toList())
            ops.clear()
            pageImageIdx.clear()
        }

        private fun ensure(altura: Double) {
            if (y - altura < MARGIN + 28) newPage()
        }

        fun addJpeg(bytes: ByteArray): Int {
            jpegs += bytes
            val idx = jpegs.lastIndex
            pageImageIdx += idx
            return idx
        }

        fun drawHeader(
            logo: Pair<ByteArray, Pair<Int, Int>>?,
            banner: Pair<ByteArray, Pair<Int, Int>>?
        ) {
            // Logo à esquerda + banner colado à direita (sem “slot” centralizado
            // que abre faixa branca entre os dois).
            val gap = 8.0
            val logoMaxW = CONTENT_W * 0.28
            val logoMaxH = 70.0
            val topoMaxH = 78.0
            ensure(topoMaxH + 16)

            var logoH = 0.0
            var logoW = 0.0
            var topoH = 0.0
            val yTop = y

            logo?.let { (bytes, wh) ->
                val idx = addJpeg(bytes)
                val scale = min(logoMaxW / wh.first, logoMaxH / wh.second)
                val w = wh.first * scale
                val h = wh.second * scale
                val x = MARGIN
                val imgY = yTop - h
                ops.append("q\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
                logoH = h
                logoW = w
            }

            banner?.let { (bytes, wh) ->
                val idx = addJpeg(bytes)
                val topoLeft = if (logoW > 0) logoW + gap else 0.0
                val topoMaxW = (CONTENT_W - topoLeft).coerceAtLeast(1.0)
                val scale = min(topoMaxW / wh.first, topoMaxH / wh.second)
                val w = wh.first * scale
                val h = wh.second * scale
                val x = MARGIN + topoLeft
                val imgY = yTop - h
                ops.append("q\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
                topoH = h
            }

            if (logo == null && banner == null) {
                // Fallback textual se assets não carregarem
                ops.append("BT\n/F2 14 Tf\n$MARGIN ${yTop - 14} Td\n(${pdfEscape("HTMS ENERGIA")}) Tj\nET\n")
                ops.append(
                    "BT\n/F1 11 Tf\n$MARGIN ${yTop - 32} Td\n" +
                        "(${pdfEscape("PROCEDIMENTO INSPEÇÃO DE CAMPO DIÁRIA")}) Tj\nET\n"
                )
                logoH = 40.0
            }

            y = yTop - max(logoH, topoH) - 16
        }

        fun drawSectionTitle(texto: String) {
            ensure(28.0)
            ops.append("BT\n/F2 12 Tf\n$MARGIN ${y - 12} Td\n(${pdfEscape(texto)}) Tj\nET\n")
            y -= 26
        }

        fun drawTable(linhas: List<Pair<String, String>>) {
            val col1W = CONTENT_W * COL1_RATIO
            val col2X = MARGIN + col1W
            val col2W = CONTENT_W - col1W - 6
            val pad = 6.0
            val fontSize = 9.0
            val lineH = 11.0

            linhas.forEach { (campo, valor) ->
                val labelLines = wrap(campo, col1W - 12, fontSize)
                val valueLines = wrap(valor.ifBlank { "—" }, col2W - 12, fontSize)
                val rowH = pad * 2 + lineH * max(labelLines.size, valueLines.size)
                ensure(rowH + 2)

                val top = y
                val bottom = y - rowH
                // Borda externa + divisor (cinza claro, como APK)
                ops.append("q\n0.6 w\n0.65 0.65 0.65 RG\n")
                ops.append("$MARGIN $bottom $CONTENT_W $rowH re\nS\n")
                ops.append("$col2X $bottom m\n$col2X $top l\nS\nQ\n")

                var ty = top - pad - fontSize
                labelLines.forEach { line ->
                    ops.append("BT\n/F2 $fontSize Tf\n${MARGIN + 6} $ty Td\n(${pdfEscape(line)}) Tj\nET\n")
                    ty -= lineH
                }
                ty = top - pad - fontSize
                val isLink = valor.startsWith("http")
                valueLines.forEach { line ->
                    if (isLink) {
                        ops.append("BT\n/F1 $fontSize Tf\n0 0 1 rg\n${col2X + 6} $ty Td\n(${pdfEscape(line)}) Tj\n0 g\nET\n")
                    } else {
                        ops.append("BT\n/F1 $fontSize Tf\n${col2X + 6} $ty Td\n(${pdfEscape(line)}) Tj\nET\n")
                    }
                    ty -= lineH
                }
                y = bottom
            }
            y -= 8
        }

        suspend fun drawSignature(rotulo: String, base64: String) {
            ensure(24.0)
            ops.append("BT\n/F2 11 Tf\n$MARGIN ${y - 11} Td\n(${pdfEscape(rotulo)}) Tj\nET\n")
            y -= 20
            if (base64.isBlank()) {
                y -= 8
                return
            }
            val raw = PlatformServices.decodeBase64(base64) ?: return
            val jpeg = PlatformImage.toJpegBytes(raw)?.takeIf { isJpeg(it) } ?: return
            val size = ImageHeaders.size(jpeg) ?: return
            val boxW = CONTENT_W * 0.48
            val boxH = 100.0
            // ensure ANTES de addJpeg — senão o XObject fica na página anterior sem /Do.
            ensure(boxH + 16)
            val idx = addJpeg(jpeg)
            val bottom = y - boxH
            ops.append("q\n0.8 w\n$MARGIN $bottom $boxW $boxH re\nS\nQ\n")
            val scale = min((boxW - 12) / size.width, (boxH - 12) / size.height)
            val w = size.width * scale
            val h = size.height * scale
            val x = MARGIN + (boxW - w) / 2
            val imgY = bottom + (boxH - h) / 2
            ops.append("q\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
            y = bottom - 14
        }

        /**
         * Embutir na página atual, escalando ao espaço restante.
         *
         * Regras críticas (bug 2026-07-23 — foto no Excel, invisível no PDF):
         * 1) Nunca [ensure]/newPage] DEPOIS de [addJpeg]: o XObject ficava na
         *    página de metadados e o `/Im Do` na seguinte, sem Resources.
         * 2) Se o espaço restante for apertado para um retrato, [newPage] ANTES
         *    de [addJpeg] — Do e Resources na mesma página, foto maior.
         * 3) [assemblePdf] ainda faz merge dos `/ImN Do` do content → Resources
         *    (rede de segurança se o tracking de pageImageIdx falhar).
         */
        fun drawPhoto(jpeg: ByteArray, imgW: Int, imgH: Int) {
            val minComfortH = 360.0
            if (y - MARGIN - 30 < minComfortH) {
                newPage()
            }
            val maxW = CONTENT_W
            val maxH = (y - MARGIN - 30).coerceAtLeast(80.0)
            val scale = minOf(maxW / imgW.toDouble(), maxH / imgH.toDouble(), 1.0)
            val w = imgW * scale
            val h = imgH * scale
            // addJpeg só depois de qualquer newPage acima.
            val idx = addJpeg(jpeg)
            val x = MARGIN + (CONTENT_W - w) / 2
            val imgY = y - h
            ops.append("q\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
            y = imgY - 10
        }

        fun drawFooter() {
            val t = pdfEscape("HTMS ENERGIA — Inspeção de Campo Diária")
            ops.append("BT\n/F1 8 Tf\n$MARGIN 22 Td\n($t) Tj\nET\n")
        }
    }

    private fun wrap(text: String, maxWidth: Double, fontSize: Double): List<String> {
        val clean = text.replace('\r', ' ')
        val avg = fontSize * 0.50
        val maxChars = (maxWidth / avg).toInt().coerceAtLeast(12)
        val out = mutableListOf<String>()
        clean.split('\n').forEach { paragraph ->
            if (paragraph.isBlank()) {
                out += ""
                return@forEach
            }
            var remaining = paragraph.trim()
            while (remaining.isNotEmpty()) {
                if (remaining.length <= maxChars) {
                    out += remaining
                    break
                }
                var cut = remaining.lastIndexOf(' ', maxChars)
                if (cut < maxChars / 3) cut = maxChars
                out += remaining.take(cut).trimEnd()
                remaining = remaining.drop(cut).trimStart()
            }
        }
        return out.ifEmpty { listOf("") }
    }

    private fun assemblePdf(pageDescs: List<PageDesc>, jpegPayloads: List<ByteArray>): ByteArray {
        val pages = pageDescs.ifEmpty { listOf(PageDesc("")) }
        val totalPages = pages.size
        val fontRegular = 3
        val fontBold = 4
        val firstImageNum = 5
        val firstPageNum = firstImageNum + jpegPayloads.size

        fun pageObjNum(i: Int) = firstPageNum + i * 2
        fun contentObjNum(i: Int) = pageObjNum(i) + 1
        fun imageObjNum(i: Int) = firstImageNum + i

        val kids = (0 until totalPages).joinToString(" ") { "${pageObjNum(it)} 0 R" }
        val outObjs = linkedMapOf<Int, ByteArray>()
        outObjs[1] = latin1("<< /Type /Catalog /Pages 2 0 R >>")
        outObjs[2] = latin1("<< /Type /Pages /Kids [ $kids ] /Count $totalPages >>")
        outObjs[fontRegular] = latin1(
            "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>"
        )
        outObjs[fontBold] = latin1(
            "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>"
        )

        jpegPayloads.forEachIndexed { idx, jpeg ->
            val size = ImageHeaders.size(jpeg)!!
            val dict = latin1(
                "<< /Type /XObject /Subtype /Image " +
                    "/Width ${size.width} /Height ${size.height} " +
                    "/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode " +
                    "/Length ${jpeg.size} >>\nstream\n"
            )
            outObjs[imageObjNum(idx)] = dict + jpeg + latin1("\nendstream")
        }

        pages.forEachIndexed { i, desc ->
            val pNum = pageObjNum(i)
            val cNum = contentObjNum(i)
            // Rede de segurança: Resources deve incluir TODO /ImN Do do stream,
            // não só o que pageImageIdx rastreou (evita foto “fantasma”).
            val indices = (desc.imageIndices + imageIndicesFromContent(desc.contentStream))
                .distinct()
                .filter { it in jpegPayloads.indices }
            val xobjects = if (indices.isEmpty()) {
                ""
            } else {
                indices.joinToString(" ") { imgIdx ->
                    "/Im${imgIdx + 1} ${imageObjNum(imgIdx)} 0 R"
                }.let { "/XObject << $it >> " }
            }
            val resources =
                "<< /Font << /F1 $fontRegular 0 R /F2 $fontBold 0 R >> $xobjects>>"
            outObjs[pNum] = latin1(
                "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W.toInt()} ${PAGE_H.toInt()}] " +
                    "/Contents $cNum 0 R /Resources $resources >>"
            )
            val streamBytes = latin1(desc.contentStream)
            outObjs[cNum] = latin1("<< /Length ${streamBytes.size} >>\nstream\n") +
                streamBytes +
                latin1("\nendstream")
        }

        // Info dict — permite verificar no PDF qual build gerou (vs cache antigo).
        val infoNum = firstPageNum + totalPages * 2
        outObjs[infoNum] = latin1(
            "<< /Producer (${pdfEscape("ICD-JpegPdfBuilder $PDF_BUILD_ID")}) " +
                "/Creator (${pdfEscape("InspecaoCampoDiaria")}) >>"
        )

        return writePdf(outObjs, infoNum)
    }

    private fun writePdf(objs: Map<Int, ByteArray>, infoObjNum: Int? = null): ByteArray {
        val maxObj = objs.keys.maxOrNull() ?: 0
        val body = ArrayList<Byte>()
        body += latin1("%PDF-1.4\n%\u00e2\u00e3\u00cf\u00d3\n").toList()
        val offsets = IntArray(maxObj + 1)
        for (n in 1..maxObj) {
            offsets[n] = body.size
            val content = objs[n] ?: latin1("<< >>")
            body += latin1("$n 0 obj\n").toList()
            body += content.toList()
            body += latin1("\nendobj\n").toList()
        }
        val xrefPos = body.size
        val xref = StringBuilder()
        xref.append("xref\n0 ${maxObj + 1}\n")
        xref.append("0000000000 65535 f \n")
        for (n in 1..maxObj) {
            xref.append(offsets[n].toString().padStart(10, '0')).append(" 00000 n \n")
        }
        val infoRef = if (infoObjNum != null) " /Info $infoObjNum 0 R" else ""
        xref.append("trailer\n<< /Size ${maxObj + 1} /Root 1 0 R$infoRef >>\n")
        xref.append("startxref\n").append(xrefPos).append("\n%%EOF")
        body += latin1(xref.toString()).toList()
        return body.toByteArray()
    }

    private fun pdfEscape(s: String): String = buildString(s.length + 8) {
        for (ch in s) {
            val c = toWinAnsi(ch)
            when (c) {
                '\\'.code -> append("\\\\")
                '('.code -> append("\\(")
                ')'.code -> append("\\)")
                in 32..126 -> append(c.toChar())
                in 128..255 -> append("\\").append(c.toString(8).padStart(3, '0'))
                else -> append('?')
            }
        }
    }

    private fun toWinAnsi(ch: Char): Int {
        val code = ch.code
        if (code in 0..255) return code
        return when (ch) {
            '–', '—' -> '-'.code
            '“', '”', '„' -> '"'.code
            '‘', '’' -> '\''.code
            '…' -> '.'.code
            else -> '?'.code
        }
    }

    private fun latin1(s: String): ByteArray =
        ByteArray(s.length) { i ->
            val c = s[i].code
            (if (c in 0..255) c else '?'.code).toByte()
        }
}
